import cn from 'classnames'
import {FC, ReactNode} from 'react'

import {Text, TextFontWeight} from '@/components/common/content/Text'
import {FormTooltip} from '@/components/common/form/tooltip/FormTooltip'
import {CheckboxMark} from '@/components/common/icons/CheckboxMark'
import {RadioButtonMark} from '@/components/common/icons/RadioButtonMark'
import {JurisdictionFlag} from '@/components/common/jurisdiction/JurisdictionFlag'
import {LocalizedNumberFormat} from '@/components/common/LocalizedNumberFormat'
import {useTrademarkVerificationContent} from '@/content/trademarkVerification'
import {useTranslation} from '@/i18n'
import {CHECKBOX_TRANSITION, EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {Jurisdiction} from '@/utils/countries/constants'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

type MarketsOptionCardProps = {
  jurisdiction: Jurisdiction
  title: string
  price: number
  isSelected: boolean
  euIncluded?: boolean
  isDisabled?: boolean
  disabledText?: string
  tooltipText: ReactNode
  onChange: () => void
  dataTest?: string
  className?: string
  inputType?: 'checkbox' | 'radio'
  hasError?: boolean
  notSelectable?: boolean
}

export const MarketsOptionCard: FC<React.PropsWithChildren<MarketsOptionCardProps>> = ({
  jurisdiction,
  title,
  price,
  isSelected,
  euIncluded = false,
  isDisabled = false,
  disabledText,
  tooltipText,
  onChange,
  dataTest,
  className,
  inputType = 'checkbox',
  hasError,
  notSelectable = false,
}) => {
  const {t} = useTranslation('page')
  const {euPriceDescription} = useTrademarkVerificationContent().markets

  const isDisabledCard = euIncluded || isDisabled || notSelectable

  return (
    <>
      <li
        className={cn(
          'item',
          (isSelected || euIncluded) && 'selected',
          isDisabledCard && 'disabled',
          hasError && 'error',
          className,
        )}
        data-test={dataTest}
      >
        <label className="card">
          <input
            type={inputType}
            disabled={isDisabledCard}
            checked={isSelected}
            onChange={onChange}
          />
          {!notSelectable && (
            <div className="checkbox" data-test="Checkbox">
              {inputType === 'checkbox' ? (
                <CheckboxMark isChecked={isSelected || euIncluded} isDisabled={isDisabledCard} />
              ) : (
                <RadioButtonMark isChecked={isSelected} />
              )}
            </div>
          )}
          <div className="wrapper">
            <div className="image-wrapper">
              <JurisdictionFlag jurisdiction={jurisdiction} isOutlined />
            </div>
            <Text
              element="p"
              fontWeight={TextFontWeight.semibold}
              color={isDisabledCard ? theme.palette.gray[400] : theme.palette.text.default}
            >
              {title}
            </Text>
          </div>
          <div className="description">
            {isDisabledCard ? (
              <Text color={theme.palette.gray[400]}>{disabledText || t(euPriceDescription)}</Text>
            ) : (
              <div className="wrapper price-wrapper">
                <div className="price">
                  <Text color={theme.palette.text.light}>
                    <LocalizedNumberFormat value={price} type="currency" />
                  </Text>
                </div>
                <FormTooltip tooltipText={tooltipText} />
              </div>
            )}
          </div>
        </label>
      </li>

      <style jsx>{`
        .item {
          position: relative;
          flex: 1 0 calc(100% / 2 - ${theme.spacing[2]} * 2);
          max-width: calc(100% / 2 - ${theme.spacing[2]} * 2);
          margin: ${theme.spacing[3]} ${theme.spacing[2]} 0 ${theme.spacing[2]};
          background-color: ${theme.palette.background.default};
          border-radius: 12px;
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[200]};
          transition: box-shadow ${CHECKBOX_TRANSITION}ms ease-in-out;
        }
        .item:not(.disabled):hover {
          background-color: ${theme.palette.gray[50]};
        }
        .selected {
          box-shadow: inset 0px 0px 0px 2px ${theme.palette.gray[900]};
        }
        .disabled {
          background-color: ${theme.palette.gray[100]};
          box-shadow: inset 0px 0px 0px 2px ${theme.palette.gray[100]};
        }
        .error {
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.error.main};
        }
        .card {
          display: block;
          width: 100%;
          height: 100%;
          padding: ${theme.spacing[3]};
          cursor: pointer;
        }
        .disabled .card {
          cursor: default;
        }
        .wrapper {
          display: flex;
          margin-right: ${theme.spacing[4.5]};
        }
        .price-wrapper {
          align-items: center;
        }
        .description {
          margin-top: calc(${theme.spacing[1]} / 2);
        }
        .wrapper,
        .description {
          user-select: none;
        }
        .image-wrapper {
          margin-top: ${theme.spacing[0.5]};
          margin-right: ${theme.spacing[2]};
        }
        .price {
          margin-right: ${theme.spacing[1]};
        }
        .checkbox {
          position: absolute;
          top: 16px;
          right: 16px;
        }
        input {
          position: absolute;
          opacity: 0;
          cursor: pointer;
          height: 0;
          width: 0;
        }
        .hidden {
          max-height: 0;
          opacity: 0;
          margin-top: 0;
          visibility: hidden;
        }
        .expanded {
          max-height: 150px;
          margin-top: ${theme.spacing[3]};
          opacity: 1;
          visibility: visible;
          transition: opacity ${EXPAND_TRANSITION}ms ease-in-out;
        }
        @media ${MOBILE_SCREENSIZE} {
          .item {
            flex: 1 0 100%;
            max-width: 100%;
            margin-left: 0;
            margin-right: 0;
          }
        }
      `}</style>
    </>
  )
}
